import { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { useTranslation } from "react-i18next";
import BrainEyesSVG from "../../assets/images/illustrations/brain-eyes.svg";
import LetterSVG from "../../assets/images/illustrations/letter.svg";
import { Button } from "../../components/ui/Button";
import LogoHeader from "../../components/app/headers/LogoHeader";
import Navbar from "../../components/app/navbars/Navbar";
import Loader from "../../components/Loader";
import ScrollableLayout from "../../components/layout/ScrollableLayout";
import DesktopAuthLayout from "../../components/layout/DesktopAuthLayout";
import { isDesktopLayout } from "../../common/hooks/useIsDesktopView";
import { apiClient } from "../../services/api";

type Status = "validating" | "confirmed" | "alreadyValidated" | "invalid";

export default function ConfirmEmailPage() {
  const { t } = useTranslation();
  const history = useHistory();
  const [status, setStatus] = useState<Status>("validating");

  useEffect(() => {
    const url = new URL(globalThis.location.href);
    const hash = url.searchParams.get("hash");
    const email = url.searchParams.get("email");

    if (!hash || !email) {
      setStatus("invalid");
      return;
    }

    apiClient
      .post("/webapp/public/auth/validate-email", { json: { email, hash } })
      .then((response) => {
        setStatus(response.body.status);
      })
      .catch(() => {
        setStatus("invalid");
      });
  }, []);

  const isSuccess = status === "confirmed" || status === "alreadyValidated";

  const content =
    status === "validating" ? (
      <Loader expanded />
    ) : (
      <>
        <img
          className="mt-[24px] mb-[48px]"
          src={isSuccess ? LetterSVG : BrainEyesSVG}
          alt=""
        />

        <span className="text-center text-[24px] font-bold leading-[120%]">
          {status === "confirmed" && t("Email confirmed!")}
          {status === "alreadyValidated" && t("Email already confirmed")}
          {status === "invalid" && t("Oops!")}
        </span>

        <span className="my-[8px_24px]  text-center leading-[140%]">
          {status === "confirmed" &&
            t("Your email address has been successfully confirmed.")}
          {status === "alreadyValidated" &&
            t("This email has already been confirmed. You're all set.")}
          {status === "invalid" &&
            t("This confirmation link is invalid or has expired.")}
        </span>

        <Button
          variant="primary"
          size="lg"
          className="w-full"
          onClick={() => {
            history.replace("/");
          }}
        >
          {t("Back to Juisci")}
        </Button>
      </>
    );

  if (isDesktopLayout()) {
    return (
      <DesktopAuthLayout>
        <div className="flex flex-col items-center text-center text-primary-black">
          {content}
        </div>
      </DesktopAuthLayout>
    );
  }

  return (
    <ScrollableLayout header={<LogoHeader />} footer={<Navbar />}>
      <div className="flex-1 px-[40px] py-[21px] flex flex-col items-center text-center text-primary-black">
        {content}
      </div>
    </ScrollableLayout>
  );
}
